import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { QRCodeSVG } from 'qrcode.react';
import {
  Box, Typography, Paper, LinearProgress, Chip,
  CircularProgress, Alert, Button, Dialog, DialogTitle,
  DialogContent, DialogActions, TextField, IconButton,
  Stack, Divider, Avatar,
} from '@mui/material';
import WifiOffIcon         from '@mui/icons-material/WifiOff';
import LoginIcon           from '@mui/icons-material/Login';
import LogoutIcon          from '@mui/icons-material/Logout';
import CoffeeIcon          from '@mui/icons-material/Coffee';
import PlayArrowIcon       from '@mui/icons-material/PlayArrow';
import QrCode2Icon         from '@mui/icons-material/QrCode2';
import LockOutlinedIcon    from '@mui/icons-material/LockOutlined';
import CheckCircleIcon     from '@mui/icons-material/CheckCircle';
import ArrowBackIcon       from '@mui/icons-material/ArrowBack';
import CloseIcon           from '@mui/icons-material/Close';
import AccessTimeIcon      from '@mui/icons-material/AccessTime';

const API_URL = process.env.REACT_APP_API_URL || '';
const STEP_S  = 10;

// ─── Thënie motivuese (rrotullohen çdo 30s) ──────────────────────────────────
const QUOTES = [
  { text: 'Suksesi nuk është çelësi i lumturisë. Lumturia është çelësi i suksesit.', author: 'Albert Schweitzer' },
  { text: 'Puna e bërë me dashuri është arti më i lartë.', author: 'Kahlil Gibran' },
  { text: 'Çdo ditë është një mundësi e re për të qenë më i mirë se dje.', author: '' },
  { text: 'Vetëm ai që nuk punon nuk gabon. Mos ki frikë të provosh.', author: '' },
  { text: 'Njeriu i madh bëhet i madh nga qëndrimi i tij, jo nga rrethanat.', author: 'Marcus Aurelius' },
  { text: 'Ekipi i mirë nuk ka nevojë për udhëheqës të mëdhenj — ai e udhëheq veten.', author: '' },
  { text: 'Disiplina është ura midis qëllimeve dhe arritjeve.', author: 'Jim Rohn' },
  { text: 'Cilësia nuk është kurrë e rastësishme; ajo gjithmonë është rezultat i përpjekjes së menduar.', author: 'John Ruskin' },
  { text: 'Jepja gjithçka çdo dite. E nesërmja do të njohin vetveten.', author: '' },
  { text: 'Gëzimi i punës bën të përsosur çdo gjë.', author: 'Aristoteli' },
  { text: 'Bashkë arrijmë ato që nuk mund t\'i arrijmë vetëm.', author: '' },
  { text: 'Suksesi vjen te ata që janë shumë të zënë për ta kërkuar.', author: 'Henry David Thoreau' },
  { text: 'Nëse mundesh ta ëndërrosh, mundesh ta realizosh.', author: 'Walt Disney' },
  { text: 'Punoni me qëllim, jo vetëm me orë.', author: '' },
  { text: 'Respekti fitohet, ndershmëria vlerësohet, besimi fitohiet — mos i nënvlerëso kurrë.', author: '' },
];

const QUOTE_INTERVAL = 30000; // 30s

const DAYS_SQ  = ['E Diel', 'E Hënë', 'E Martë', 'E Mërkurë', 'E Enjte', 'E Premte', 'E Shtunë'];
const MONTHS_SQ = ['Janar','Shkurt','Mars','Prill','Maj','Qershor','Korrik','Gusht','Shtator','Tetor','Nëntor','Dhjetor'];

// ─── Live Clock ──────────────────────────────────────────────────────────────
function LiveClock({ inverted = false }) {
  const [now, setNow] = useState(new Date());
  useEffect(() => {
    const iv = setInterval(() => setNow(new Date()), 1000);
    return () => clearInterval(iv);
  }, []);

  const hh = String(now.getHours()).padStart(2,'0');
  const mm = String(now.getMinutes()).padStart(2,'0');
  const ss = String(now.getSeconds()).padStart(2,'0');
  const day = DAYS_SQ[now.getDay()];
  const date = `${now.getDate()} ${MONTHS_SQ[now.getMonth()]} ${now.getFullYear()}`;

  const textColor  = inverted ? 'rgba(255,255,255,0.95)' : '#0F172A';
  const subColor   = inverted ? 'rgba(255,255,255,0.55)' : '#64748B';

  return (
    <Box sx={{ textAlign: inverted ? 'center' : 'right', userSelect: 'none' }}>
      <Typography sx={{
        fontSize: inverted ? '3.8rem' : '2.4rem',
        fontWeight: 700, lineHeight: 1, color: textColor,
        letterSpacing: inverted ? '-2px' : '-1px',
        fontVariantNumeric: 'tabular-nums',
        fontFamily: '"Inter", system-ui, sans-serif',
      }}>
        {hh}<Box component="span" sx={{
          animation: 'blink 1s step-start infinite',
          '@keyframes blink': { '0%,100%': { opacity: 1 }, '50%': { opacity: 0.2 } },
        }}>:</Box>{mm}
        {inverted && (
          <Box component="span" sx={{ fontSize: '2rem', color: 'rgba(255,255,255,0.5)', ml: 0.5 }}>{ss}</Box>
        )}
      </Typography>
      <Typography sx={{ fontSize: '0.78rem', color: subColor, mt: 0.2, fontWeight: 500, letterSpacing: 0.3 }}>
        {day}, {date}
      </Typography>
    </Box>
  );
}

// ─── Paneli motivues (right side) ────────────────────────────────────────────
function MotivationalPanel() {
  const [idx, setIdx] = useState(() => Math.floor(Math.random() * QUOTES.length));
  const [fade, setFade] = useState(true);

  useEffect(() => {
    const iv = setInterval(() => {
      setFade(false);
      setTimeout(() => {
        setIdx(i => (i + 1) % QUOTES.length);
        setFade(true);
      }, 500);
    }, QUOTE_INTERVAL);
    return () => clearInterval(iv);
  }, []);

  const q = QUOTES[idx];

  return (
    <Box sx={{
      flex: 1, display: 'flex', flexDirection: 'column',
      alignItems: 'center', justifyContent: 'space-between',
      p: 5,
      background: 'linear-gradient(160deg, #0f0c29 0%, #1e1b4b 40%, #302b63 75%, #24243e 100%)',
      position: 'relative', overflow: 'hidden',
    }}>
      {/* Subtle glow blobs */}
      <Box sx={{
        position: 'absolute', width: 320, height: 320, borderRadius: '50%',
        background: 'radial-gradient(circle, rgba(99,102,241,0.18) 0%, transparent 70%)',
        top: -80, right: -80, pointerEvents: 'none',
      }} />
      <Box sx={{
        position: 'absolute', width: 240, height: 240, borderRadius: '50%',
        background: 'radial-gradient(circle, rgba(139,92,246,0.12) 0%, transparent 70%)',
        bottom: 60, left: -60, pointerEvents: 'none',
      }} />

      {/* Clock section top */}
      <Box sx={{ width: '100%', display: 'flex', flexDirection: 'column', alignItems: 'center', pt: 2 }}>
        <LiveClock inverted />
      </Box>

      {/* Divider */}
      <Box sx={{ width: 48, height: 2, borderRadius: 1, bgcolor: 'rgba(255,255,255,0.12)', my: 2 }} />

      {/* Quote section */}
      <Box sx={{
        flex: 1, display: 'flex', flexDirection: 'column',
        alignItems: 'center', justifyContent: 'center', gap: 3, px: 1,
      }}>
        {/* Open quote glyph */}
        <Typography sx={{
          fontSize: '4.5rem', lineHeight: 0.5, color: 'rgba(255,255,255,0.10)',
          fontFamily: 'Georgia, serif', userSelect: 'none', alignSelf: 'flex-start',
        }}>"</Typography>

        <Typography sx={{
          color: 'rgba(255,255,255,0.9)', fontWeight: 500, fontSize: '1rem',
          lineHeight: 1.75, textAlign: 'center', fontStyle: 'italic', letterSpacing: 0.3,
          transition: 'opacity 0.5s ease', opacity: fade ? 1 : 0,
          px: 1,
        }}>
          {q.text}
        </Typography>

        {q.author && (
          <Typography sx={{
            color: 'rgba(255,255,255,0.4)', fontSize: '0.72rem',
            fontWeight: 600, letterSpacing: 1.5, textTransform: 'uppercase',
            transition: 'opacity 0.5s ease', opacity: fade ? 1 : 0,
          }}>
            — {q.author}
          </Typography>
        )}
      </Box>

      {/* Dot indicators */}
      <Box sx={{ display: 'flex', gap: 0.7, pb: 1 }}>
        {QUOTES.map((_, i) => (
          <Box key={i} sx={{
            width: i === idx ? 20 : 6, height: 6, borderRadius: 3,
            bgcolor: i === idx ? 'rgba(255,255,255,0.8)' : 'rgba(255,255,255,0.18)',
            transition: 'width 0.4s cubic-bezier(.4,0,.2,1), background 0.4s',
          }} />
        ))}
      </Box>
    </Box>
  );
}

/**
 * Teksti i QR-së së kioskut.
 *
 * v2 mban edhe kompaninë (`c`): id-të e kioskëve nisin nga 1 te çdo bazë, ndaj
 * pa të serveri s'e dallonte dot QR-në e një kompanie tjetër nga një kod i
 * skaduar. Pa `company` (server i vjetër) mbetet formati v1.
 */
function kioskQrPayload(d) {
  return JSON.stringify(d.company
    ? { v: 2, c: d.company, k: d.kiosk_id, t: d.otp }
    : { k: d.kiosk_id, t: d.otp });
}

// ─── Kiosk QR Display ────────────────────────────────────────────────────────
export default function KioskScreen({ kioskToken, kioskTenant, deviceId }) {
  const { t } = useTranslation();
  const [qrData,    setQrData]    = useState(null);
  const [timeLeft,  setTimeLeft]  = useState(STEP_S);
  const [loading,   setLoading]   = useState(true);
  const [offline,   setOffline]   = useState(false);
  const fetchRef    = useRef(false);
  const retryTimer  = useRef(null);

  // ── Fallback login state ─────────────────────────────────
  const [fbOpen,       setFbOpen]       = useState(false);
  const [fbPhase,      setFbPhase]      = useState('login'); // 'login' | 'action' | 'success'
  const [fbEmail,      setFbEmail]      = useState('');
  const [fbPass,       setFbPass]       = useState('');
  const [fbLoading,    setFbLoading]    = useState(false);
  const [fbError,      setFbError]      = useState('');
  const [fbWorker,     setFbWorker]     = useState(null); // { workerId, name }
  const [fbSuccess,    setFbSuccess]    = useState('');
  const successTimer = useRef(null);

  const ACTIONS = [
    { action: 'checkin',     label: t('kiosk.actionCheckin'),     icon: <LoginIcon />,      color: '#16a34a', bg: '#dcfce7', darkBg: '#14532d' },
    { action: 'break_start', label: t('kiosk.actionBreakStart'),  icon: <CoffeeIcon />,     color: '#d97706', bg: '#fef3c7', darkBg: '#78350f' },
    { action: 'break_end',   label: t('kiosk.actionBreakEnd'),    icon: <PlayArrowIcon />,  color: '#2563eb', bg: '#dbeafe', darkBg: '#1e3a8a' },
    { action: 'checkout',    label: t('kiosk.actionCheckout'),    icon: <LogoutIcon />,     color: '#dc2626', bg: '#fee2e2', darkBg: '#7f1d1d' },
  ];

  const openFallback = () => {
    setFbPhase('login');
    setFbEmail('');
    setFbPass('');
    setFbError('');
    setFbWorker(null);
    setFbSuccess('');
    setFbOpen(true);
  };

  const closeFallback = () => {
    setFbOpen(false);
    clearTimeout(successTimer.current);
  };

  const handleFbLogin = async (e) => {
    e.preventDefault();
    if (!fbEmail || !fbPass) { setFbError(t('kiosk.fillEmailPassword')); return; }
    setFbLoading(true);
    setFbError('');
    try {
      const r   = await fetch(`${API_URL}/api/auth/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: fbEmail, password: fbPass }),
      });
      const d = await r.json();
      if (!d.success) { setFbError(d.error || t('kiosk.invalidCredentials')); return; }
      const user = d.data?.user;
      if (!user?.worker_id) {
        setFbError(t('kiosk.noWorkerLinked'));
        return;
      }
      const worker = {
        workerId: user.worker_id,
        name: [user.firstname, user.lastname].filter(Boolean).join(' ') || user.email,
        token: d.data?.token,
      };
      setFbWorker(worker);
      setFbPhase('action');
    } catch {
      setFbError(t('kiosk.serverConnectionError'));
    } finally {
      setFbLoading(false);
    }
  };

  /**
   * Hyrja manuale nuk e anashkalon rregullin e QR-së — e plotëson ndryshe.
   *
   * Kiosku e di OTP-në e vet të çastit, sepse vetë e shfaq si QR. Duke ia
   * dërguar atë `verify-scan`-it me token-in e punëtorit, merr të njëjtën
   * biletë që do të merrte një telefon që skanoi ekranin. Prova e pranisë
   * mbetet e njëjtë; ndryshon vetëm kush e lexon kodin.
   */
  const doAction = async (worker, action) => {
    setFbLoading(true);
    setFbError('');
    try {
      const auth = { 'Content-Type': 'application/json', Authorization: `Bearer ${worker.token}` };

      let scanTicket;
      if (qrData?.kiosk_id && qrData?.otp) {
        const v = await fetch(`${API_URL}/api/qr/phone/verify-scan`, {
          method: 'POST',
          headers: auth,
          body: JSON.stringify({ qr: kioskQrPayload(qrData) }),
        }).then((res) => res.json()).catch(() => null);
        scanTicket = v?.scanTicket;
      }

      const r = await fetch(`${API_URL}/api/attendance/direct-action`, {
        method: 'POST',
        headers: auth,
        body: JSON.stringify({
          worker_id: worker.workerId,
          action,
          ...(scanTicket ? { scan_ticket: scanTicket } : {}),
        }),
      });
      const d = await r.json();
      if (!d.success) { setFbError(d.error || t('kiosk.registrationError')); setFbPhase('action'); return; }
      const actionLabel = ACTIONS.find(a => a.action === action)?.label || action;
      setFbSuccess(t('kiosk.actionSuccess', { name: worker.name, action: actionLabel }));
      setFbPhase('success');
      successTimer.current = setTimeout(() => {
        setFbOpen(false);
      }, 3500);
    } catch {
      setFbError(t('kiosk.serverConnectionError'));
      setFbPhase('action');
    } finally {
      setFbLoading(false);
    }
  };

  const handleFbAction = async (action) => {
    if (fbWorker) await doAction(fbWorker, action);
  };

  useEffect(() => () => clearTimeout(successTimer.current), []);

  const fetchKioskQR = useCallback(async () => {
    if (fetchRef.current) return;
    fetchRef.current = true;
    if (retryTimer.current) { clearTimeout(retryTimer.current); retryTimer.current = null; }
    try {
      const r = await fetch(`${API_URL}/api/qr/kiosk-qr`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Kiosk-Token': kioskToken,
          // Kompania e kioskut — pa të serveri e kërkon te kompania e parazgjedhur.
          ...(kioskTenant ? { 'X-Kiosk-Tenant': kioskTenant } : {}),
        },
        body: JSON.stringify({ device_id: parseInt(deviceId, 10) }),
        signal: AbortSignal.timeout(6000),
      });
      const d = await r.json();
      if (d.success) {
        setQrData(d);
        const remaining = Math.max(1, Math.ceil((d.valid_until_ms - Date.now()) / 1000));
        setTimeLeft(remaining);
        setOffline(false);
      } else {
        setOffline(true);
        // Riprovo pas 4 sekondash kur ka gabim serveri
        retryTimer.current = setTimeout(() => { fetchRef.current = false; fetchKioskQR(); }, 4000);
      }
    } catch (_) {
      setOffline(true);
      // Riprovo pas 4 sekondash kur nuk ka lidhje
      retryTimer.current = setTimeout(() => { fetchRef.current = false; fetchKioskQR(); }, 4000);
    } finally {
      setLoading(false);
      fetchRef.current = false;
    }
  }, [kioskToken, kioskTenant, deviceId]); // eslint-disable-line react-hooks/exhaustive-deps

  // Fetch i parë
  useEffect(() => {
    fetchKioskQR();
    return () => { if (retryTimer.current) clearTimeout(retryTimer.current); };
  }, [fetchKioskQR]);

  // Countdown + auto-refresh (vetëm kur online)
  useEffect(() => {
    const iv = setInterval(() => {
      setTimeLeft(prev => {
        if (prev <= 1) {
          fetchKioskQR();
          return STEP_S;
        }
        return prev - 1;
      });
    }, 1000);
    return () => clearInterval(iv);
  }, [fetchKioskQR]);

  const urgency  = timeLeft <= 3;
  const progress = (timeLeft / STEP_S) * 100;
  const qrPayload = qrData ? kioskQrPayload(qrData) : '';

  return (
    <Box sx={{
      display: 'flex', height: '100vh', bgcolor: '#F2F4F7',
      fontFamily: 'Inter, system-ui, sans-serif',
    }}>
      {/* ── Left: QR panel ── */}
      <Box sx={{
        flex: 1, display: 'flex', flexDirection: 'column',
        alignItems: 'center', justifyContent: 'center',
        p: 3, gap: 2,
      }}>
        {/* Header */}
        <Box sx={{ textAlign: 'center' }}>
          <Typography variant="h5" fontWeight={900} sx={{ color: '#101828', letterSpacing: -0.5 }}>
            {t('kiosk.registerPresence')}
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
            {t('kiosk.scanQrInstruction')}
          </Typography>
        </Box>

        {/* Offline alert */}
        {offline && (
          <Alert severity="warning" icon={<WifiOffIcon />} sx={{ width: '100%', maxWidth: 340 }}>
            {t('kiosk.serverOffline')}
          </Alert>
        )}

        {/* QR Code */}
        {loading ? (
          <CircularProgress size={60} />
        ) : (
          <Paper
            elevation={6}
            sx={{
              p: 2.5, borderRadius: 4,
              border: '4px solid',
              borderColor: offline ? '#F59E0B' : urgency ? 'error.main' : '#3E8FA3',
              transition: 'border-color 0.4s',
              animation: 'qrPop 0.3s ease',
              '@keyframes qrPop': {
                from: { transform: 'scale(0.95)', opacity: 0.5 },
                to:   { transform: 'scale(1)',    opacity: 1 },
              },
            }}
          >
            {qrPayload ? (
              <QRCodeSVG
                value={qrPayload}
                size={312}
                level="M"
                includeMargin={false}
                fgColor="#101828"
              />
            ) : (
              <Box sx={{ width: 312, height: 312, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 2 }}>
                <CircularProgress size={48} sx={{ color: '#F59E0B' }} />
                <Typography variant="caption" color="text.secondary" textAlign="center">
                  {t('kiosk.connectingServer')}
                </Typography>
              </Box>
            )}
          </Paper>
        )}

        {/* Countdown bar — pa sekonda (anti-mashtrm) */}
        {!loading && (
          <Box sx={{ width: 338, textAlign: 'center' }}>
            <LinearProgress
              variant="determinate"
              value={progress}
              color={urgency ? 'error' : 'primary'}
              sx={{ height: 6, borderRadius: 4 }}
            />
          </Box>
        )}

        {/* Kiosk name badge */}
        {qrData && (
          <Chip
            icon={<QrCode2Icon />}
            label={qrData.kiosk_name}
            variant="outlined"
            size="small"
            sx={{ borderColor: '#3E8FA3', color: '#3E8FA3', fontWeight: 700 }}
          />
        )}

        {/* Hyrje manuale — vetëm si fallback i fshehur */}
        <Box sx={{ mt: 0.5 }}>
          <Button
            size="small"
            startIcon={<LockOutlinedIcon sx={{ fontSize: '0.82rem !important' }} />}
            onClick={openFallback}
            sx={{
              color: '#c8d0da',
              fontSize: '0.68rem',
              textTransform: 'none',
              fontWeight: 400,
              py: 0.2,
              opacity: 0.5,
              '&:hover': { color: '#64748b', opacity: 1, background: 'rgba(0,0,0,0.04)' },
              transition: 'opacity 0.2s',
            }}
          >
            {t('kiosk.manualLogin')}
          </Button>
        </Box>
      </Box>

      {/* ── Right: Motivational panel ── */}
      <Box sx={{ width: { xs: 0, sm: 300 }, display: { xs: 'none', sm: 'flex' }, flexDirection: 'column' }}>
        <MotivationalPanel />
      </Box>

      {/* ── Fallback Login Modal ─────────────────────────── */}
      <Dialog
        open={fbOpen}
        onClose={closeFallback}
        maxWidth="xs"
        fullWidth
        PaperProps={{ sx: { borderRadius: 4, p: 0.5 } }}
      >
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1, pb: 1 }}>
          <LockOutlinedIcon sx={{ color: '#3E8FA3' }} />
          <Box sx={{ flex: 1 }}>
            <Typography fontWeight={800} fontSize="1.05rem">
              {t('kiosk.manualLogin')}
            </Typography>
            {fbWorker && fbPhase !== 'login' && (
              <Typography variant="caption" color="text.secondary">
                {fbWorker.name}
              </Typography>
            )}
          </Box>
          <IconButton size="small" onClick={closeFallback}><CloseIcon fontSize="small" /></IconButton>
        </DialogTitle>

        <Divider />

        <DialogContent sx={{ pt: 2.5 }}>
          {/* ── Faza 1: Login ── */}
          {fbPhase === 'login' && (
            <Box component="form" id="fb-login-form" onSubmit={handleFbLogin}>
              <Stack spacing={2.5}>
                <TextField
                  label="Email"
                  type="email"
                  value={fbEmail}
                  onChange={e => setFbEmail(e.target.value)}
                  fullWidth
                  autoFocus
                  autoComplete="email"
                  size="small"
                  required
                />
                <TextField
                  label={t('kiosk.password')}
                  type="password"
                  value={fbPass}
                  onChange={e => setFbPass(e.target.value)}
                  fullWidth
                  autoComplete="current-password"
                  size="small"
                  required
                />
                {fbError && <Alert severity="error" sx={{ py: 0.5 }}>{fbError}</Alert>}
              </Stack>
            </Box>
          )}

          {/* ── Faza 2: Zgjedh aksionin ── */}
          {fbPhase === 'action' && (
            <Stack spacing={1.5}>
              <Typography variant="body2" color="text.secondary" textAlign="center" mb={0.5}>
                {t('kiosk.whatToRegister')}
              </Typography>
              {ACTIONS.map(({ action, label, icon, color, bg }) => (
                <Button
                  key={action}
                  fullWidth
                  variant="outlined"
                  onClick={() => handleFbAction(action)}
                  disabled={fbLoading}
                  startIcon={React.cloneElement(icon, { sx: { color } })}
                  sx={{
                    borderColor: color,
                    color,
                    bgcolor: bg,
                    fontWeight: 700,
                    py: 1.2,
                    borderRadius: 3,
                    justifyContent: 'flex-start',
                    '&:hover': { bgcolor: bg, borderColor: color, opacity: 0.85 },
                    '&:disabled': { opacity: 0.5 },
                  }}
                >
                  {label}
                </Button>
              ))}
              {fbError && <Alert severity="error" sx={{ py: 0.5 }}>{fbError}</Alert>}
            </Stack>
          )}

          {/* ── Faza 3: Sukses ── */}
          {fbPhase === 'success' && (
            <Box sx={{ textAlign: 'center', py: 2 }}>
              <CheckCircleIcon sx={{ fontSize: 64, color: '#2e7d32', mb: 1.5 }} />
              <Typography fontWeight={700} color="#2e7d32" fontSize="1.1rem">
                {fbSuccess}
              </Typography>
              <Typography variant="caption" color="text.secondary" mt={1} display="block">
                {t('kiosk.autoClose')}
              </Typography>
            </Box>
          )}
        </DialogContent>

        {fbPhase !== 'success' && (
          <DialogActions sx={{ px: 3, pb: 2.5, pt: 0.5, gap: 1 }}>
            {fbPhase === 'action' && (
              <Button
                size="small"
                startIcon={<ArrowBackIcon />}
                onClick={() => { setFbPhase('login'); setFbError(''); }}
                sx={{ mr: 'auto', textTransform: 'none', color: '#64748b' }}
              >
                {t('common.back')}
              </Button>
            )}
            <Button onClick={closeFallback} color="inherit" sx={{ textTransform: 'none' }}>
              {t('common.cancel')}
            </Button>
            {fbPhase === 'login' && (
              <Button
                type="submit"
                form="fb-login-form"
                variant="contained"
                disabled={fbLoading}
                sx={{ textTransform: 'none', borderRadius: 2, bgcolor: '#3E8FA3', '&:hover': { bgcolor: '#256878' } }}
              >
                {fbLoading ? <CircularProgress size={18} sx={{ color: '#fff' }} /> : t('kiosk.loginBtn')}
              </Button>
            )}
          </DialogActions>
        )}
      </Dialog>
    </Box>
  );
}
